/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React from 'react';

const themeFontSizes = [
  "0.875rem", "0.9rem", "1rem", "1.15rem", "1.31rem",
  "1.58rem", "2.1rem", "3.15rem", "3.78rem",
] as const;

const iconSizes = {
  xs: '0.75rem',
  sm: '0.875rem',
  md: '1rem',
  lg: '1.125rem',
  xl: '1.25rem',
  '2xl': '1.5rem',
  '3xl': '1.875rem',
  '4xl': '2.25rem',
} as const;

type IconSize = keyof typeof iconSizes | number | string;

type IconProps = {
  size?: IconSize;
  icon?: any;
  as?: any;
  style?: React.CSSProperties;
  [key: string]: any;
};

/**
 * Renders an icon component at a consistent size.
 *
 * @param {IconProps} props - Icon properties.
 * @returns {JSX.Element} Rendered icon.
 */
const Icon: React.FC<IconProps> = ({ icon, as, size, style, ...props }) => {
  const IconComponent = icon || as;
  let sizeStyle: React.CSSProperties = {};

  const finalSize = size || 'md';

  if (typeof finalSize === 'number') {
    sizeStyle = { width: `${finalSize}px`, height: `${finalSize}px` };
  } else if (typeof finalSize === 'string' && iconSizes[finalSize as keyof typeof iconSizes]) {
    const sizeValue = iconSizes[finalSize as keyof typeof iconSizes];
    sizeStyle = { width: sizeValue, height: sizeValue };
  } else if (typeof finalSize === 'string' && (finalSize.endsWith('px') || finalSize.endsWith('rem'))) {
    sizeStyle = { width: finalSize, height: finalSize };
  }

  const mergedStyle: React.CSSProperties = {
    display: "inline-block",
    color: "currentColor",
    flexShrink: 0,
    verticalAlign: "middle",
    ...sizeStyle,
    ...style,
  };

  if (!IconComponent) return null;
  return <IconComponent style={mergedStyle} {...props} />;
};

export default Icon;
